import React from 'react';
import { api, ApiError } from './api';
import { Loading, ErrorBanner } from './ui';
import { AuthView, PolicyView } from './views/Auth';
import { FamilyView } from './views/Family';
import { StudentView } from './views/Student';
import { OpsView } from './views/Ops';
import { AiPreviewView } from './views/AiPreview';
import { StudentSetupView } from './views/StudentSetup';
import { AccountView } from './views/Account';
import { FaqView } from './views/Faq';
import { TutorialsView } from './views/Tutorials';
import { navigate, initializeNavigation, acceptBrowserNavigation, protectUnload } from './navigation';
export { navigate } from './navigation';

export type Principal = {
  userId: string;
  email?: string;
  displayName?: string | null;
  role: 'parent' | 'student' | 'ops_admin' | 'ops_viewer';
  familyId?: string | null;
  studentId?: string | null;
};

export type Me = {
  authenticated: boolean;
  principal?: Principal;
  consents?: { type: string; granted_at: string; policy_version: string; student_id?: string | null }[];
  supportEmail?: string;
  policyVersion?: string;
  earlyReleaseDisclaimerVersion?: string;
  policyVersionRequired?: string[];
};

type Route =
  | { name: 'family' }
  | { name: 'policy'; kind: 'terms' | 'privacy' }
  | { name: 'faq' }
  | { name: 'tutorials' }
  | { name: 'student'; id: string }
  | { name: 'ops' }
  | { name: 'ai-preview' }
  | { name: 'account' }
  | { name: 'student-setup' };

function parseRoute(): Route {
  const path = window.location.pathname;
  if (path === '/terms') return { name: 'policy', kind: 'terms' };
  if (path === '/privacy') return { name: 'policy', kind: 'privacy' };
  if (path === '/faq') return { name: 'faq' };
  if (path === '/tutorials') return { name: 'tutorials' };
  if (path.startsWith('/student-setup')) return { name: 'student-setup' };
  const m = path.match(/^\/students\/([0-9a-f-]{36})/i);
  if (m) return { name: 'student', id: m[1] };
  if (path.startsWith('/ops')) return { name: 'ops' };
  if (path.startsWith('/ai-preview')) return { name: 'ai-preview' };
  if (path.startsWith('/account')) return { name: 'account' };
  return { name: 'family' };
}

export function App() {
  const [route, setRoute] = React.useState<Route>(parseRoute);
  const [me, setMe] = React.useState<Me | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    initializeNavigation();
    const onPop = (event: PopStateEvent) => {
      if (acceptBrowserNavigation()) setRoute(parseRoute());
      else event.stopImmediatePropagation();
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('beforeunload', protectUnload);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('beforeunload', protectUnload);
    };
  }, []);

  const loadMe = React.useCallback(async () => {
    setError(null);
    try {
      setMe(await api<Me>('GET', '/api/me'));
    } catch (e) {
      const error = e as ApiError;
      if (error.status === 401) setMe({ authenticated: false });
      else setError(error.message);
    }
  }, []);

  React.useEffect(() => {
    void loadMe();
  }, [loadMe]);

  if (error) return <div className="app"><ErrorBanner error={error} onRetry={loadMe} /></div>;
  if (!me) return <div className="app"><Loading what="your account" /></div>;

  // The student setup link is redeemed without an existing session.
  if (route.name === 'student-setup') {
    return <div className="app"><StudentSetupView me={me} onAuth={loadMe} /></div>;
  }

  if (!me.authenticated) {
    if (route.name === 'policy') return <PolicyView kind={route.kind} />;
    if (route.name === 'faq') return <FaqView />;
    if (route.name === 'tutorials') return <TutorialsView />;
    return <div className="app"><AuthView onAuth={loadMe} /></div>;
  }

  const p = me.principal!;
  const isOps = p.role === 'ops_admin' || p.role === 'ops_viewer';
  const logout = async () => { await api('POST', '/api/auth/logout'); loadMe(); navigate('/'); };
  const effectiveRoute = isOps && route.name === 'family'
    ? { name: 'ops' as const }
    : !isOps && route.name === 'ai-preview'
      ? { name: 'family' as const }
      : route;
  // The student's own view gets a warmer, more energetic palette; parents and
  // ops staff keep the calmer default. See [data-experience] rules in styles.css.
  const experience = isOps ? 'ops' : p.role === 'student' ? 'student' : 'parent';

  return (
    <div className="app" data-experience={experience}>
      <header className="top">
        <div className="row" style={{ gap: 12 }}>
          <a className="brand-lockup-sm brand-home-link" aria-label="Student Assistant home page" href="https://studentassistant.net/">
            <span className="brand-mark-sm" aria-hidden="true">SA</span>
            <span className="brand-wordmark-sm">Student Assistant</span>
          </a>
          <div>
            <strong>{isOps ? 'Operations Command Center' : p.role === 'student' ? 'My Student Assistant' : 'Family Command Center'}</strong>
            <div className="muted">
              {p.displayName || p.email || 'student'} · {p.role === 'parent' ? 'Parent account' : p.role}
            </div>
          </div>
        </div>
        <nav className="tabs" aria-label="Sections">
          {!isOps && (
            <button
              aria-current={effectiveRoute.name === 'family' ? 'page' : undefined}
              onClick={() => navigate('/')}
            >
              {p.role === 'student' ? 'My worker' : 'Family home'}
            </button>
          )}
          {isOps && (
            <button aria-current={effectiveRoute.name === 'ops' ? 'page' : undefined} onClick={() => navigate('/ops')}>
              Operations
            </button>
          )}
          {isOps && <button aria-current={effectiveRoute.name === 'ai-preview' ? 'page' : undefined} onClick={() => navigate('/ai-preview')}>AI preview</button>}
          {p.role === 'parent' && <button aria-current={effectiveRoute.name === 'account' ? 'page' : undefined} onClick={() => navigate('/account')}>Account</button>}
          <button aria-current={effectiveRoute.name === 'tutorials' ? 'page' : undefined} onClick={() => navigate('/tutorials')}>Tutorials</button>
          <button aria-current={effectiveRoute.name === 'faq' ? 'page' : undefined} onClick={() => navigate('/faq')}>FAQ</button>
          <button onClick={logout}>Sign out</button>
        </nav>
      </header>

      {effectiveRoute.name === 'family' && <FamilyView me={me} onChanged={loadMe} />}
      {effectiveRoute.name === 'student' && <StudentView key={`${p.role}:${effectiveRoute.id}`} studentId={effectiveRoute.id} me={me} onChanged={loadMe} />}
      {effectiveRoute.name === 'ops' && <OpsView />}
      {effectiveRoute.name === 'ai-preview' && <AiPreviewView />}
      {effectiveRoute.name === 'account' && <AccountView onClosed={() => { loadMe(); navigate('/'); }} />}
      {effectiveRoute.name === 'faq' && <FaqView embedded />}
      {effectiveRoute.name === 'tutorials' && <TutorialsView />}
    </div>
  );
}
